/* eslint-disable @typescript-eslint/no-floating-promises */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Center,
  Grid,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  Spinner,
  Text,
  useDisclosure,
  Icon,
  Button,
  Flex,
} from '@chakra-ui/core';
import { useTranslation } from 'react-i18next';
import { FiDownload, FiRefreshCw } from 'react-icons/fi';
import colors from '../../../../../../styles/colors';
import { AnswerFile, fileKind, getDocumentFileName, getViewerUrl } from '../documentRenderer.types';
import FilePreview from './FilePreview';
import ImageCarousel from './ImageCarousel';
import { useStoreState } from '../../../../../../models/hooks';

export interface AnswerRendererProps {
  answers: AnswerFile[];
  maxItemsPerRow?: number;
}

export const AnswerRenderer: React.FC<AnswerRendererProps> = ({ answers, maxItemsPerRow = 2 }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState<AnswerFile | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [iframeLoading, setIframeLoading] = useState<boolean>(true);
  const [previewFailed, setPreviewFailed] = useState<boolean>(false);
  const [retryCount, setRetryCount] = useState<number>(0);

  // Cache blob URLs per file URL to avoid redundant fetches
  const blobUrlCache = useRef<Map<string, string>>(new Map());
  const loadingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClose = useCallback(() => {
    // istanbul ignore else
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
    onClose();
  }, [onClose]);

  useEffect(() => {
    const cache = blobUrlCache.current;
    return () => {
      cache.forEach((blobUrl) => window.URL.revokeObjectURL(blobUrl));
      cache.clear();
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    };
  }, []);

  const appUserId = useStoreState((s) => s.app.user?.id);
  const appOwnersId = useStoreState((s) => s.app.accounts?.owners);
  const superOwners = useStoreState((s) => s.app.accounts?.superOwners);

  // Memoize admin checks — only recompute when relevant store values change
  const canDownload = useMemo(() => {
    const isAdmin = appOwnersId && appUserId ? appOwnersId.includes(appUserId) : false;
    const isSuperAdmin = superOwners && appUserId ? superOwners.includes(appUserId) : false;
    return isAdmin || isSuperAdmin;
  }, [appUserId, appOwnersId, superOwners]);

  // filters image list — only recomputes when `answers` changes
  const imageFiles = useMemo(() => answers.filter((f) => fileKind(f) === 'image'), [answers]);

  const gridTemplateColumns = useMemo(() => `repeat(${Math.min(answers.length, maxItemsPerRow)}, 140px)`, [
    answers.length,
    maxItemsPerRow,
  ]);

  // avoids calling fileKind on every render
  const selectedType = useMemo(() => (selectedFile ? fileKind(selectedFile) : 'other'), [selectedFile]);

  // opening a file preview
  const openPreview = useCallback(
    (file: AnswerFile) => {
      const index = imageFiles.findIndex((f) => f.url === file.url);
      setSelectedIndex(index >= 0 ? index : 0);
      setSelectedFile(file);
      setIframeLoading(true);
      setPreviewFailed(false);
      setRetryCount(0);
      onOpen();

      // Detect if Google Docs Viewer fails to load (e.g., 204 No Content or download triggered)
      // by checking if the iframe load event hasn't fired after 5 seconds
      // istanbul ignore next
      if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = setTimeout(() => {
        setIframeLoading(false);
        setPreviewFailed(true);
      }, 10000);
    },
    [imageFiles, onOpen],
  );
  /* istanbul ignore next */
  const handleIframeLoad = useCallback(() => {
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    const delay = selectedType === 'pdf' ? 2000 : 300;
    const timer = setTimeout(() => {
      setIframeLoading(false);
      setPreviewFailed(false);
    }, delay);
    return () => clearTimeout(timer); // returned value unused by onLoad, but safe
  }, [selectedType]);

  const handleRetry = useCallback(() => {
    /* istanbul ignore else */
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    setIframeLoading(true);
    setPreviewFailed(false);
    setRetryCount((c) => c + 1);

    /* istanbul ignore else */
    if (loadingTimeoutRef.current) clearTimeout(loadingTimeoutRef.current);
    loadingTimeoutRef.current = setTimeout(() => {
      setIframeLoading(false);
      setPreviewFailed(true);
    }, 10000);
  }, []);

  // carousel index change
  const handleCarouselIndexChange = useCallback((idx: number) => setSelectedFile(imageFiles[idx]), [imageFiles]);

  // Download with blob URL caching — avoids re-fetching the same file
  const handleDownload = useCallback(() => {
    /* istanbul ignore if */
    if (!selectedFile) return;

    const fileUrl = selectedFile.url;
    const fileName =
      getDocumentFileName(selectedFile.name) /* istanbul ignore next */ ||
      fileUrl.split('/').pop() /* istanbul ignore next */ ||
      'download';

    const cached = blobUrlCache.current.get(fileUrl);

    const triggerDownload = (blobUrl: string) => {
      const alink = document.createElement('a');
      alink.href = blobUrl;
      alink.download = fileName;
      document.body.appendChild(alink);
      alink.click();
      document.body.removeChild(alink);
    };

    if (cached) {
      triggerDownload(cached);
      return;
    }

    fetch(fileUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        blobUrlCache.current.set(fileUrl, blobUrl);
        triggerDownload(blobUrl);
      })
      .catch(() => {
        triggerDownload(fileUrl);
      });
  }, [selectedFile]);

  const handleDownloadClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleDownload();
    },
    [handleDownload],
  );

  const showDownloadButton = useMemo(
    () => canDownload && selectedFile != null && (selectedType === 'word' || selectedType === 'pdf') && !iframeLoading,
    [canDownload, selectedFile, selectedType, iframeLoading],
  );

  const iframeStyle = useMemo<React.CSSProperties>(
    () => ({
      border: 'none',
      backgroundColor: 'white',
      marginLeft: selectedType === 'pdf' ? '60px' : '0px',
      marginRight: selectedType === 'pdf' ? '80px' : '0px',
      width: selectedType === 'pdf' ? 'calc(100% - 140px)' : '100%',
      height: '100%',
      opacity: iframeLoading ? 0 : 1,
      transition: 'opacity 0.2s ease-in-out',
    }),
    [selectedType, iframeLoading],
  );

  return (
    <>
      <Box bg="#4B5D68" borderRadius="10px" p={3} width="fit-content" maxW="100%" overflow="hidden">
        <Text color="white" fontSize="xs" fontWeight="600" mb={3}>
          {t('common:documentUploadQuestions.totalDocuments', { count: answers.length })}
        </Text>

        <Grid templateColumns={gridTemplateColumns} gap={3} style={{ gridTemplateColumns }}>
          {answers.map((file) => {
            return (
              <Box key={`${file.name}`} w="140px" flexShrink={0}>
                <FilePreview file={file} onOpen={() => openPreview(file)} />
              </Box>
            );
          })}
        </Grid>
      </Box>

      <Modal isOpen={isOpen} onClose={handleClose} size="4xl" isCentered>
        <ModalOverlay />

        <ModalContent>
          <ModalHeader>{getDocumentFileName(selectedFile?.name ?? '')}</ModalHeader>

          <ModalCloseButton />

          <ModalBody pb={4}>
            {selectedType === 'image' && imageFiles.length > 0 && (
              <ImageCarousel
                files={imageFiles}
                initialIndex={selectedIndex}
                height="75vh"
                objectFit="contain"
                showDownload={canDownload}
                onChangeIndex={handleCarouselIndexChange}
              />
            )}

            {selectedFile && selectedType !== 'image' && (
              <Box height="75vh" position="relative" overflow="hidden" borderRadius="lg" bg="white">
                {iframeLoading && !previewFailed && (
                  <Center position="absolute" inset={0} zIndex={20} bg="rgba(255,255,255,0.9)">
                    <Spinner size="xl" color={colors.blue[400]} />
                  </Center>
                )}

                {previewFailed && (
                  <Center
                    position="absolute"
                    inset={0}
                    zIndex={20}
                    bg="rgba(255,255,255,0.95)"
                    flexDirection="column"
                    p={6}
                    textAlign="center"
                  >
                    <Text fontSize="lg" fontWeight="500" color="gray.700" mb={2}>
                      {t('common:documentUploadQuestions.previewFailed')}
                    </Text>
                    <Text fontSize="sm" color="gray.500" mb={6}>
                      {t('common:documentUploadQuestions.previewFailedDesc')}
                    </Text>
                    <Flex direction="row">
                      <Button leftIcon={<FiRefreshCw />} colorScheme="blue" onClick={handleRetry} mr={3}>
                        {t('common:button.retry')}
                      </Button>
                      {showDownloadButton && (
                        <Button leftIcon={<FiDownload />} variant="outline" colorScheme="blue" onClick={handleDownloadClick}>
                          {t('common:button.downloadFile')}
                        </Button>
                      )}
                    </Flex>
                  </Center>
                )}

                <Box
                  position="absolute"
                  left={0}
                  top={0}
                  bottom={0}
                  width="60px"
                  bg="white"
                  boxShadow="inset -8px 0 12px rgba(0,0,0,0.08)"
                  zIndex={10}
                  pointerEvents="none"
                />

                <Box
                  position="absolute"
                  right={0}
                  top={0}
                  bottom={0}
                  width="80px"
                  bg="white"
                  boxShadow="inset 8px 0 12px rgba(0,0,0,0.08)"
                  zIndex={10}
                >
                  <Flex position="absolute" right={2} top={2} direction="column" zIndex={30}>
                    {showDownloadButton && (
                      <Button
                        mb={selectedType === 'word' ? 2 : 0}
                        bg="#fff"
                        borderRadius="6px"
                        p={2}
                        onClick={handleDownloadClick}
                        cursor="pointer"
                        _hover={{ bg: '#f0f0f0' }}
                        aria-label="Download document"
                        data-testid="downloadBtn"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        boxShadow="md"
                      >
                        <Icon as={FiDownload} color="#000" size={8} />
                      </Button>
                    )}
                  </Flex>
                </Box>

                <iframe
                  key={`${selectedFile.url}-${retryCount}`}
                  src={`${getViewerUrl(selectedFile)}${selectedType === 'word' && retryCount > 0 ? `&retry=${retryCount}` : ''}`}
                  title={getDocumentFileName(selectedFile.name)}
                  frameBorder="0"
                  onLoad={handleIframeLoad}
                  style={iframeStyle}
                />
              </Box>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default AnswerRenderer;
